import React, { useCallback, useMemo } from 'react';
import { BranchFormAnswer, FormAnswers } from '../../types';
import Animation from '../Shared/Animation/Animation';
import useAnimatedPages from '../../hooks/useAnimatedPages';
import { vars } from '../../reactive';
import { Question } from './FormQuestion/types';
import FormNonLazyQuestionsRender from './FormNonLazyQuestionsRender';
import FormLazyQuestionsRender from './FormLazyQuestionsRender';

const FormQuestionsCategory = ({
  questionsSplitByCategory,
  formsAnswers,
  allQuestions,
  navigateToNextCategory,
  navigateToPreviousCategory,
  defaultCategoryPageIndex
}: {
  questionsSplitByCategory: { QuestionCategoryId: string; questions: Question[] };
  formsAnswers: FormAnswers[];
  allQuestions: Question[];
  navigateToNextCategory: () => void;
  navigateToPreviousCategory: () => void;
  defaultCategoryPageIndex: number;
}) => {
  const { currentPage: currentCategoryPage, increaseCurrentPage: increaseCurrentCategoryPage, decreaseCurrentPage: decreaseCurrentCategoryPage } = useAnimatedPages(defaultCategoryPageIndex);

  const nonLazyQuestions = useMemo(() => {
    return questionsSplitByCategory.questions.filter(({ question }) => question.get_answers_from === null);
  }, [questionsSplitByCategory]);

  const lazyQuestions = useMemo(() => {
    return questionsSplitByCategory.questions.filter(({ question }) => question.get_answers_from !== null);
  }, [questionsSplitByCategory]);

  const createNewAnswer = ({ formId, questionId, petRecordId, answer }: { formId: string; questionId: string; petRecordId: string | null; answer: unknown }): FormAnswers['answers'][0] => {
    const question = allQuestions.find(question => question.question.id === questionId && question.formId === formId)!;
    const hasAnswerOptions = !!question?.question.AnswerOptions?.length;
    const fixedAnswer = question?.question.type === 'RADIO' || question?.question.type === 'DROPDOWN' ? [answer] : answer;
    const newAnswer = hasAnswerOptions
      ? { AnswerOptionId: fixedAnswer as BranchFormAnswer['AnswerOptionId'], PetRecordId: petRecordId, answer: null }
      : { answer: fixedAnswer as BranchFormAnswer['answer'], PetRecordId: petRecordId, AnswerOptionId: null };
    return newAnswer;
  };

  const addNewAnswer = (formId: string, questionId: string, answers: BranchFormAnswer[]) => {
    const newAnswers = [...formsAnswers, { formId, questionId, answers }];
    vars.formsAnswers(newAnswers);
  };

  const addNewFormAnswersAnswer = (formId: string, questionId: string, answers: BranchFormAnswer[]) => {
    const newAnswers = formsAnswers.map(answer => {
      if (answer.questionId === questionId && answer.formId === formId) {
        return { ...answer, answers };
      }
      return answer;
    });

    vars.formsAnswers(newAnswers);
  };

  const nextPage = () => {
    if (currentCategoryPage === questionsSplitByCategory.questions.length - 1) {
      navigateToNextCategory();
      return;
    }
    increaseCurrentCategoryPage();
  };

  const onQuestionSubmit = useCallback(
    ({ formId, questionId, petRecordId, answer }: { formId: string; questionId: string; petRecordId: string | null; answer: any }) => {
      const existingAnswers = formsAnswers.find(answer => answer.questionId === questionId && answer.formId === formId)?.answers;
      if (!existingAnswers) {
        const answers = [createNewAnswer({ formId, questionId, petRecordId, answer })];
        addNewAnswer(formId, questionId, answers);
        nextPage();
        return;
      }

      const answerForPetRecord = existingAnswers?.find(answer => answer.PetRecordId === petRecordId);
      if (!answerForPetRecord) {
        const newFormAnswers = [...existingAnswers, createNewAnswer({ formId, questionId, petRecordId, answer })];
        addNewFormAnswersAnswer(formId, questionId, newFormAnswers);
        nextPage();
        return;
      }

      const newFormAnswers = existingAnswers.map(a => {
        if (a.PetRecordId === petRecordId) {
          return createNewAnswer({ formId, questionId, petRecordId, answer });
        }
        return a;
      });
      addNewFormAnswersAnswer(formId, questionId, newFormAnswers);
      nextPage();
    },
    [formsAnswers, allQuestions, increaseCurrentCategoryPage]
  );

  const previousPage = {
    onPrevious: () => {
      if (currentCategoryPage === 0) {
        navigateToPreviousCategory();
        return;
      }

      decreaseCurrentCategoryPage();
    }
  };

  return (
    <Animation animationKey={currentCategoryPage}>
      <>
        {currentCategoryPage <= nonLazyQuestions.length - 1 && (
          <FormNonLazyQuestionsRender questions={nonLazyQuestions} currentPage={currentCategoryPage} onQuestionSubmit={onQuestionSubmit} previousPage={previousPage} formsAnswers={formsAnswers} />
        )}
        {currentCategoryPage >= nonLazyQuestions.length && (
          <FormLazyQuestionsRender
            questions={lazyQuestions}
            currentPage={currentCategoryPage}
            onQuestionSubmit={onQuestionSubmit}
            previousPage={previousPage}
            formsAnswers={formsAnswers}
            nonLazyQuestionsLength={nonLazyQuestions.length}
          />
        )}
      </>
    </Animation>
  );
};

export default FormQuestionsCategory;
